import {
  JobListResponse,
  SavedSearchResponse,
} from '@doda/api-client/aspida/@types';
import { apiClient } from '@doda/api-client/util/apiClient';
import {
  AppQueryParams,
  QueryOtherParams,
} from '@doda/common/src/constants/queryParams';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { createSearchConditionRequestParams } from 'logics/createSearchConditionRequestParams';
import { IncomingHttpHeaders } from 'http';
import { Methods as JobSearchListRequestType } from '@doda/api-client/aspida/api/v1/jobs';
import { convertSaveSearchesToJobSearches } from 'logics/savedSearches';
import {
  isErrorResponseBody,
  nonNullable,
} from '@doda/common/src/utils/typeGuard';
import { createNearAreaJobListRequestParams } from '@doda/common/src/logics/createNearAreaJobListRequestParams';
import { getNearAreaJobList } from '@doda/common/src/services/getNearAreaJobList';
import { setValidationMessages } from '..';

/**
 * RejectedValue
 */
export interface RejectedValue {
  message: string;
  redirect: boolean;
}

// TODO: src/constants/jobList で定義してる JOB_COUNT_PER_PAGE に差し替える
export const DISPLAY_NUMBER = 50;

/**
 * Fetches the job list based on provided query parameters.
 *
 * @param {Object} params - Parameters for fetching job list.
 * @param {IncomingHttpHeaders} params.headers - Headers for the request.
 * @param {AppQueryParams} params.query - Query parameters for fetching job list.
 * @param {QueryOtherParams} params.queryother - Additional query parameters.
 * @returns {Promise<JobListResponse>} - Job list response.
 */
export const fetchJobList = createAsyncThunk<
  JobListResponse,
  {
    headers?: IncomingHttpHeaders;
    query: AppQueryParams;
    queryother?: QueryOtherParams;
  },
  {
    rejectValue: RejectedValue;
  }
>('jobList/fetchJobList', async ({ headers, query, queryother }, thunkAPI) => {
  try {
    if (query.nearArea) {
      const param = createNearAreaJobListRequestParams(query);
      const nearAreaJobList = await getNearAreaJobList(
        { ...param, limit: DISPLAY_NUMBER },
        headers
      );
      return nearAreaJobList;
    }
    const param = createSearchConditionRequestParams(query, queryother);
    const jobSearchList = await apiClient.api.v1.jobs.$get({
      query: {
        ...param,
      },
      config: { headers },
    });
    return jobSearchList;
  } catch (error) {
    const axiosError = error as AxiosError;
    // バリデーションチェックに失敗した場合
    if (axiosError.response?.status === 400) {
      if (isErrorResponseBody(axiosError.response?.data)) {
        const validationMessages = axiosError.response.data.details
          .map(({ message }) => message)
          .filter(nonNullable);
        thunkAPI.dispatch(setValidationMessages(validationMessages));
      }
      return thunkAPI.rejectWithValue({
        message: `jobList/fetchJobList status 400, api request path: ${decodeURI(
          axiosError.request?.path
        )}`,
        redirect: false,
      });
    }
    return thunkAPI.rejectWithValue({ message: '', redirect: false });
  }
});

/**
 * Fetches the job list based on saved searches.
 *
 * @param {Object} params - Parameters for fetching saved searches job list.
 * @param {IncomingHttpHeaders} params.headers - Headers for the request.
 * @param {Omit<SavedSearchResponse, 'no' | 'name' | 'savedDate'>} params.savedSearches - Saved searches data.
 * @param {JobSearchListRequestType['get']['query']} [params.optionParam] - Optional additional query parameters.
 * @returns {Promise<JobListResponse>} - Job list response.
 */
export const fetchSaveSearchesJobList = createAsyncThunk<
  JobListResponse,
  {
    headers?: IncomingHttpHeaders;
    savedSearches: Omit<SavedSearchResponse, 'no' | 'name' | 'savedDate'>;
    optionParam?: JobSearchListRequestType['get']['query'];
  },
  {
    rejectValue: RejectedValue;
  }
>(
  'jobList/fetchSaveSearchesJobList',
  async ({ headers, savedSearches, optionParam }, thunkAPI) => {
    try {
      const params = convertSaveSearchesToJobSearches(savedSearches);
      const jobSearchList = await apiClient.api.v1.jobs.$get({
        query: {
          ...params,
          limit: DISPLAY_NUMBER,
          ...optionParam,
        },
        config: { headers },
      });
      return jobSearchList;
    } catch (error) {
      const axiosError = error as AxiosError;
      // バリデーションチェックに失敗した場合
      if (axiosError.response?.status === 400) {
        if (isErrorResponseBody(axiosError.response?.data)) {
          const validationMessages = axiosError.response.data.details
            .map(({ message }) => message)
            .filter(nonNullable);
          thunkAPI.dispatch(setValidationMessages(validationMessages));
        }
        return thunkAPI.rejectWithValue({
          message: `jobList/fetchSaveSearchesJobList status 400, api request path: ${decodeURI(
            axiosError.request?.path
          )}`,
          redirect: false,
        });
      }
      return thunkAPI.rejectWithValue({ message: '', redirect: false });
    }
  }
);
